import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  GraduationCap,
  Users,
  Calendar,
  DollarSign,
  BarChart,
  Shield,
  ArrowRight,
  Star,
  CheckCircle,
  Smartphone,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import Marquee from 'react-fast-marquee';
import {
  PlayStoreIcon,
  AppStoreIcon,
  PLAY_STORE_URL,
  APP_STORE_URL,
} from '@/components/StoreIcons';

export default function HomePage() {
  const features = [
    {
      icon: Users,
      title: 'Student Management',
      description: 'Complete student lifecycle management from admission to graduation',
    },
    {
      icon: Calendar,
      title: 'Smart Scheduling',
      description: 'Automated timetable generation and class management',
    },
    {
      icon: DollarSign,
      title: 'Fee Management',
      description: 'Streamlined fee collection with automated reminders',
    },
    {
      icon: BarChart,
      title: 'Analytics & Reports',
      description: 'Real-time insights and comprehensive reporting',
    },
  ];

  const benefits = [
    'Save 15+ hours weekly on administrative tasks',
    'Reduce paper work by 90%',
    'Real-time parent communication',
    'Secure cloud-based data storage',
    'Mobile app for parents & students',
    'Automated attendance tracking',
  ];

  const testimonials = [
    {
      instituteName: 'Shri Hit Gyanvati Education Training Institute',
      logoSrc: '/shrihit_logo.png',
      logoAlt: 'Shri Hit Gyanvati Education Training Institute logo',
      directorName: 'Director',
      text: 'VibhumEduSys transformed our school operations completely. Highly recommended!',
      rating: 5,
    },
    {
      instituteName: 'Swami Madhuvandas Tyagi School',
      logoSrc: '/swami_logo.png',
      logoAlt: 'Swami Madhuvandas Tyagi School logo',
      directorName: 'Swami Madhuvandas Tyagi',
      text: 'The best investment we made for our institution. Parent communication improved drastically.',
      rating: 5,
    },
    {
      instituteName: 'Saraswati Vidya Mandir',
      logoSrc: '/saraswati_logo.jpg',
      logoAlt: 'Saraswati Vidya Mandir logo',
      directorName: 'Principal',
      text: 'Fee management and student tracking became so easy. Great software!',
      rating: 5,
    },
    {
      instituteName: 'Army Public School, Bharatpur',
      logoSrc: '/army_logo.png',
      logoAlt: 'Army Public School logo',
      directorName: 'Principal',
      text: 'VibhumEduSys has streamlined our daily school operations. From student records to fee management, everything is organized, efficient, and easy to access.',
      rating: 5,
    },
  ];

  return (
    <div data-testid="home-page" className="pt-20">
      <section
        data-testid="hero-section"
        className="py-24 md:py-32 px-6 md:px-12 lg:px-24"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-block bg-emerald-light/10 border border-emerald/20 px-4 py-2 rounded-full mb-6">
                <span className="text-emerald font-medium text-sm">
                  AI-Powered School Management
                </span>
              </div>
              <h1 className="text-5xl sm:text-6xl tracking-tighter font-black text-navy mb-6 font-heading">
                Transform Your School With Smart Management
              </h1>
              <p className="text-base leading-relaxed text-slate-600 mb-8">
                Complete cloud-based school management solution trusted by 10+
                schools across India. Automate administration, enhance parent
                communication, and focus on what matters - education.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button
                    data-testid="hero-demo-button"
                    size="lg"
                    className="bg-emerald hover:bg-emerald-light text-white font-semibold px-8 cta-button"
                  >
                    Start Free Trial
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button
                    data-testid="hero-trial-button"
                    size="lg"
                    variant="outline"
                    className="border-2 border-navy text-navy hover:bg-slate-50 font-semibold px-8"
                  >
                    See Pricing
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-200">
                <img
                  // src="https://images.pexels.com/photos/27141316/pexels-photo-27141316.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  src="/transform_image.webp"
                  alt="Dashboard Preview"
                  className="w-full h-auto"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl border border-slate-200">
                <div className="flex items-center space-x-3">
                  <div className="bg-emerald-light/10 p-3 rounded-lg">
                    <Shield className="h-6 w-6 text-emerald" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-navy">100% Secure</p>
                    <p className="text-xs text-slate-500">Cloud-based storage</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section
        data-testid="features-preview-section"
        className="py-24 bg-slate-50"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-navy mb-4 font-heading">
              Everything You Need To Manage Your School
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              Powerful features designed to simplify school administration and
              improve efficiency
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                data-testid={`feature-card-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm feature-card"
              >
                <div className="bg-emerald-light/10 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-emerald" />
                </div>
                <h3 className="text-xl font-semibold text-navy mb-2 font-heading">
                  {feature.title}
                </h3>
                <p className="text-sm text-slate-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/features">
              <Button
                data-testid="view-all-features-button"
                variant="outline"
                size="lg"
                className="border-2 border-navy text-navy hover:bg-slate-50 font-semibold"
              >
                View All Features
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section data-testid="benefits-section" className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-navy mb-6 font-heading">
                Why Schools Choose VibhumEduSys
              </h2>
              <p className="text-base text-slate-600 mb-8">
                Join hundreds of schools that have transformed their operations
                with our intelligent management platform.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    data-testid={`benefit-item-${index}`}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-center space-x-3"
                  >
                    <CheckCircle className="h-6 w-6 text-emerald flex-shrink-0" />
                    <span className="text-slate-700">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                // src="https://images.pexels.com/photos/36159725/pexels-photo-36159725.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                src="/school.jpeg"
                alt="Modern School"
                className="rounded-2xl shadow-xl border border-slate-200"
              />
            </div>
          </div>
        </div>
      </section>

      <section
        data-testid="app-availability-section"
        className="py-24 bg-gradient-to-br from-navy to-slate-800 text-white"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <Smartphone className="h-16 w-16 text-emerald-light mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl tracking-tight font-bold mb-4 font-heading">
            Available on Android & iOS
          </h2>
          <p className="text-base text-slate-300 mb-8 max-w-2xl mx-auto">
            Access VibhumEduSys anywhere, anytime. Mobile apps for students,
            parents, and staff with all essential features at your fingertips.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              onClick={() => window.open(PLAY_STORE_URL, '_blank')}
              data-testid="android-app-button"
              size="icon"
              aria-label="Download on Google Play"
              title="Download on Google Play"
              className="h-14 w-14 bg-black hover:bg-slate-100 active:bg-slate-200 rounded-xl transition-colors active:scale-95 shadow-md"
            >
              <PlayStoreIcon className="!size-8" />
            </Button>
            <Button
              onClick={() => window.open(APP_STORE_URL, '_blank')}
              data-testid="ios-app-button"
              size="icon"
              aria-label="Download on App Store"
              title="Download on App Store"
              className="h-14 w-14 bg-black hover:bg-neutral-800 active:bg-neutral-700 rounded-xl transition-colors active:scale-95 shadow-md border-0 text-white"
            >
              <AppStoreIcon className="!size-8" />
            </Button>
          </div>
        </div>
      </section>

      <section
        data-testid="testimonials-section"
        className="py-24 bg-slate-50 overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 mb-12">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-navy text-center mb-4 font-heading">
            Trusted By Leading Institutions
          </h2>
          <p className="text-base text-slate-600 text-center">
            See what school leaders are saying about VibhumEduSys
          </p>
        </div>

        <Marquee
          gradient={false}
          speed={50}
          pauseOnHover={true}
          autoFill
          className="pl-4"
        >
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              data-testid={`testimonial-card-${index}`}
              className="mr-8 shrink-0 bg-white p-8 rounded-2xl border border-slate-200 shadow-sm w-96 flex flex-col"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="h-16 w-16 rounded-xl border border-slate-200 bg-white flex items-center justify-center p-1.5 flex-shrink-0 overflow-hidden">
                  <img
                    src={testimonial.logoSrc}
                    alt={testimonial.logoAlt}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-navy text-sm leading-snug font-heading">
                    {testimonial.instituteName}
                  </p>
                  <p className="text-sm text-slate-600 mt-1">
                    {testimonial.directorName}
                  </p>
                </div>
              </div>
              <div className="flex space-x-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star
                    key={i}
                    className="h-5 w-5 fill-yellow-400 text-yellow-400"
                  />
                ))}
              </div>
              <p className="text-slate-700 mb-0 italic flex-1">
                "{testimonial.text}"
              </p>
            </div>
          ))}
        </Marquee>
      </section>

      <section data-testid="cta-section" className="py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-navy mb-6 font-heading">
            Ready To Transform Your School?
          </h2>
          <p className="text-base text-slate-600 mb-8">
            Join 500+ schools already using VibhumEduSys. Get started with a free
            demo today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button
                data-testid="cta-demo-button"
                size="lg"
                className="bg-emerald hover:bg-emerald-light text-white font-semibold px-8 cta-button"
              >
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                data-testid="cta-contact-button"
                size="lg"
                variant="outline"
                className="border-2 border-navy text-navy hover:bg-slate-50 font-semibold px-8"
              >
                Talk to Expert
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
